import { IMaintenanceTrigger, ImeasurementPointAnswer } from '../models';

export interface MaintenanceFieldValues {
    isEnabled: boolean;
    maintenanceCompleted: boolean;
    isSnoozed: boolean;
    manualEntryDate: string | undefined;
    isUnknown: boolean;
}

/**
 * Resolve the editable maintenance fields. Precedence: in-session pending edit, then the saved MP
 * answer (the job's source of truth), then the install-base trigger / a default. Shared by
 * MaintenanceMPControl (for display) and the Save & Log seeding validation so both agree.
 */
export const resolveMaintenanceFieldValues = (
    pending: IMaintenanceTrigger | undefined,
    answer: ImeasurementPointAnswer | undefined,
    triggerFallback: IMaintenanceTrigger | undefined
): MaintenanceFieldValues => ({
    isEnabled: pending?.isEnabled ?? answer?.isEnabled ?? triggerFallback?.isEnabled ?? false,
    isSnoozed: pending?.isSnoozed ?? answer?.isSnoozed ?? triggerFallback?.isSnoozed ?? false,
    maintenanceCompleted: pending?.maintenanceCompleted ?? (answer ? answer.pass === 1 : false),
    manualEntryDate: pending?.manualEntryDate ?? answer?.dateValue,
    isUnknown: pending?.isUnknown ?? answer?.isUnknown ?? false
});

export interface MaintenanceTriggerValidationContext {
    maintenanceType: number;
    // Optional props include `| undefined` explicitly so callers can pass possibly-undefined values
    // (e.g. Array.find results, an explicit `answer: undefined`) under exactOptionalPropertyTypes.
    pending?: IMaintenanceTrigger | undefined;
    answer?: ImeasurementPointAnswer | undefined;
    synced?: IMaintenanceTrigger | undefined;
}

/**
 * "Initial seeding" failure: an enabled trigger with no maintenance recorded this job (WMC=No, no
 * Manual Entry Date, not flagged Unknown) AND nothing on the server yet (no LastCompletedDate and no
 * NextDueDate). Such a trigger would be enabled with no NextDueDate and silently never surface. Any
 * one escape valve (WMC=Yes, a date, Unknown, or disabling) clears it; existing history clears it
 * too, including a prior Unknown, which the server keeps as a back-dated NextDueDate with no
 * LastCompletedDate (MMG-4430).
 */
export const isMaintenanceTriggerUnseeded = (ctx: MaintenanceTriggerValidationContext): boolean => {
    const values = resolveMaintenanceFieldValues(ctx.pending, ctx.answer, ctx.synced);
    return isManualEntryDateRequired(ctx) && !values.manualEntryDate;
};

/**
 * Date Last Maintained is required whenever an enabled trigger has no other way to seed itself -
 * the same rule as isMaintenanceTriggerUnseeded, minus whether the date has actually been filled
 * in. Drives the required asterisk on the field's label (MMG-4448).
 */
export const isManualEntryDateRequired = (ctx: MaintenanceTriggerValidationContext): boolean => {
    const values = resolveMaintenanceFieldValues(ctx.pending, ctx.answer, ctx.synced);
    const hasPreviousData = !!ctx.synced?.lastCompletedDate || !!ctx.synced?.nextDueDate;
    return values.isEnabled && !values.maintenanceCompleted && !values.isUnknown && !hasPreviousData;
};

/** Maintenance types that fail the seeding check (empty = valid). */
export const findUnseededMaintenanceTriggers = (
    contexts: MaintenanceTriggerValidationContext[]
): number[] => contexts.filter(isMaintenanceTriggerUnseeded).map(c => c.maintenanceType);
